import { ReactNode } from "react";
import { CitizenCodes, Experience, Pronouns, StudentAward } from "../../../api/client";
import { formatExperienceType } from "./StudentDataFormatter";

export const DefaultProfileNonReportedText: string = 'N/A (Was not provided)'
export const DefaultProfileNonReportedField: ReactNode = <span className='text-slate-500 dark:text-slate-300'>{DefaultProfileNonReportedText}</span>
import { stringifyAwardStatus } from "../../../helpers/stringHelpers";
import { PenFill, TrashFill } from "react-bootstrap-icons";

interface StudentProfileRowFormatProps {
    field?: ReactNode;
    value?: ReactNode; // robust styling
    separator?: boolean; // whether or not the row is just a separator
}

export function StudentProfileRowFormat({field, value, separator}: StudentProfileRowFormatProps): JSX.Element {
    if (separator) {
        return (
            <> 
            {/* It has to be this empty wrapper, else the two lines below will be grouped into one item and mess up the intended 2-column format of the Student Profile Card*/}
                <div className="mt-2"></div> {/* In the place of field */}
                <div className="mt-2"></div> {/* In the place of value */}
            </>
        )
    }

    return (
        <>
            <div className="font-bold">{field ?? "Placeholder Field"}:</div>
            <div className="flex flex-row gap-1 items-center">
                {value ?? DefaultProfileNonReportedText}
            </div>
        </>
    )
}

export function formatPronouns(pronounsEnum: Pronouns) {
    switch (pronounsEnum) {
        case Pronouns.Unknown:
            return DefaultProfileNonReportedField
        case Pronouns.SheHer:
            return "She/Her"
        case Pronouns.HeHim:
            return "He/Him";
        case Pronouns.TheyThem:
            return "They/Them";
        case Pronouns.Other:
            return "Other"
    }
}

interface StudentTagFormatProps {
    value: ReactNode; // robust styling
}

export function StudentTagFormat({value}: StudentTagFormatProps): JSX.Element {
    return (
        <div className="text-md bg-green-600 text-white px-3 py-1 rounded-full mr-2 mb-2 inline-block">
            {value}
        </div>
    )
}

export function formatCitizenCodes(citizenCodesEnum: CitizenCodes) {
    switch (citizenCodesEnum) {
        case CitizenCodes.CitizenUSA:
            return "U.S. Citizen"
        case CitizenCodes.ForeignNational:
            return "Foreign National";
        case CitizenCodes.PermResident:
            return "Permanent Resident";
        case CitizenCodes.Unknown:
            return "Unknown"
    }
}

interface StudentCardSectionHeaderProps {
    fromDW?: boolean;
}

export function StudentCardSectionHeader({fromDW}: StudentCardSectionHeaderProps) {
    return (
        <>{fromDW ?
            <div className="mb-4 text-slate-700 dark:text-slate-200">
                The information below is pulled from <span className="font-semibold">USF’s database</span> and <span className="font-semibold">cannot be edited</span> in the Office of National Scholars Portal.
            </div>
        :
            <div className="mb-4 text-slate-700 dark:text-slate-200">
                The information below is <span className="font-semibold">self-reported</span> during student onboarding and <span className="font-semibold">can be edited</span>.
            </div>
        }</>
    )
}

interface StudentExperienceCardProps {
    index: number;
    elem: Experience;
}

export function StudentExperienceCard({index, elem}: StudentExperienceCardProps) {
    return (
        <div key={index} className='border-2 dark:border-slate-400 rounded-xl p-3'>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-1">
                <span>
                <span className="text-base font-bold sm:text-lg">
                    {elem.title}
                </span>
                {elem.type && (
                    <span className="text-sm text-gray-600 dark:text-slate-300">
                    {" "}
                    · {formatExperienceType(elem.type)}
                    </span>
                )}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-300">
                {elem.yearsNumber} {(elem.yearsNumber! > 1) ? `Years` : `Year`}
                </span>
            </div>

            {/* Role */}
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                Role · {elem.role}
            </p>
                        
            {/* Mentor */}
            {elem.mentor && <p className="text-sm text-slate-500 dark:text-slate-300">
                Mentor · {elem.mentor}
            </p>}
                        
            {/* Description */}
            {elem.description && <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap dark:text-white">
                {elem.description}
            </p>}
        </div>
    )
}

interface StudentAwardCardProps {
    index: number;
    elem: StudentAward;
    onDelete: (id: string) => void; // when delete is pressed
    onEdit: (award: StudentAward) => void; // when edit is pressed
    isAdmin: boolean; // whether to show edit controls or not
}

export function StudentAwardCard({index, elem, onDelete, onEdit, isAdmin}: StudentAwardCardProps) {
    return (
        <div key={index} className='border-2 dark:border-slate-400 rounded-xl p-3'>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-1">
                <span>
                    <span className="text-base font-bold sm:text-lg">
                        {elem.award?.name}
                    </span>
                    {elem.program && (
                        <span className="text-sm text-gray-600 dark:text-slate-300">
                        {" "}
                        · {elem.isUndergraduate ? 'Undergraduate' : 'Graduate'}
                        </span>
                    )}
                    </span>
                <span className="text-sm text-slate-500 dark:text-slate-300">
                {elem.fullAcademicYear}
                </span>
            </div>

            <p className="text-md mb-3 text-slate-600 dark:text-slate-100">
                {elem.college?.name}
            </p>

            {/* Program */}
            <p className="mt-1 text-md text-slate-500 dark:text-slate-300">
                {elem.program?.name} {elem.programDetails ? `· ${elem.programDetails}` : ''}
            </p>
            
            {/* Estimated Amount */}
            <p className="mt-1 text-md text-slate-500 dark:text-slate-300">
                Amount · { elem.estimatedAmount ? `\$${elem.estimatedAmount}` : 'N/A'}
            </p>

            {/* Award Status */}
            <p className="mt-1 text-md text-slate-500 dark:text-slate-300">
                Status · {stringifyAwardStatus(elem.awardStatus!)}
            </p>
            
            {isAdmin && <div className="flex items-end justify-end space-x-2">
                <button
                    onClick={() => {onEdit(elem)}}
                    className="flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-100 transition-all"
                >
                    <PenFill className="w-5 h-5"/>
                </button>
                <button
                    onClick={() => {onDelete(elem.id!)}}
                    className="flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                >
                    <TrashFill className="w-5 h-5"/>
                </button>
            </div>}
        </div>
    )
}